import React, { useEffect, useState } from 'react';
import { Carousel } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';
import AppShell from '../Components/AppShell';
import Container from '../Components/Container';
import axiosInstance from '../utils/axiosInstance';
import { runSafely } from '../utils/runSafely';
import { getAuthToken } from '../utils/storageUtil';
import { formatNumber } from '../utils/numberUtil';

// NOTE: the commission carousel below is commented out. Restoring it needs
// its import back: import AutoSwipe from '../Components/AutoSwipe';

interface ShowcaseProduct {
    id: number;
    name: string;
    seller: string;
    price: number;
    url: string;
}

/** How many products the showcase displays. */
const SHOWCASE_COUNT = 2;

/**
 * The three commodity images. One list, rendered as a slider below lg and as
 * the three-up grid at lg.
 */
const COMMODITY_IMAGES = [
    '/images/bonia/bonia-front-store.webp',
    '/images/bonia/BONIA_Store_front.webp',
    '/images/bonia/bonia_sign.jpg',
];

/**
 * The two hero calls to action. Both point at routes the app already has —
 * "discover more" to the product detail page, "shop the edit" to the order
 * flow, the same destination the showcase tiles below use.
 */
const HERO_CTAS = [
    { key: 'discover', labelKey: 'discover_more', path: '/detail' },
    { key: 'shop', labelKey: 'shop_the_edit', path: '/order' },
];

/** Outlined-on-image button, the retail convention for a hero action. */
const HERO_CTA_CLASS =
    'border border-solid border-white/80 bg-transparent text-white cursor-pointer ' +
    'px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] whitespace-nowrap ' +
    'transition-colors duration-200 hover:bg-white hover:text-brand-900 ' +
    'active:bg-white active:text-brand-900 ' +
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
    'focus-visible:outline-accent-500 ' +
    'lg:px-8 lg:py-3.5 lg:text-xs lg:tracking-[0.2em]';

const HomePage: React.FC = () => {
    const [showcase, setShowcase] = useState<ShowcaseProduct[]>([]);

    const navigate = useNavigate();
    const { t } = useTranslation();

    /**
     * Reads the catalogue off the endpoint the authenticated pages already
     * call. `/protected/getUserInfo` returns `allProducts` (already filtered to
     * isActive) alongside the user payload, so the showcase needs no new route
     * and no public product endpoint — /home is behind auth either way.
     */
    const getShowcase = async () => {
        const res = await axiosInstance.get("/auth/products");
        const products = res.data?.data;
        if (!Array.isArray(products)) return;

        // Highest-priced first: a showcase leads with the premium end of the
        // catalogue. `slice()` first so the response array is not sorted in place.
        const top = products
            .slice()
            .sort((a: any, b: any) => b.price - a.price)
            .slice(0, SHOWCASE_COUNT)
            .map((e: any) => ({
                id: e.id,
                name: e.name,
                seller: e.seller,
                price: e.price,
                url: process.env.REACT_APP_FOLDER_URL + "/" + e.img,
            }));

        setShowcase(top);
    };

    // Mount-only fetch; see AccountPage for why the dependency is omitted.
    //
    // Skipped entirely for a signed-out visitor. /home is public, but the
    // showcase reads `/protected/getUserInfo`, which answers 401 without a
    // token — and axiosInstance turns a 401 into `window.location.href =
    // '/login'`. Firing it anonymously would therefore throw the visitor
    // straight back to the login form, which is exactly what this page is
    // meant to avoid. The section below is guarded on `showcase.length`, so
    // leaving it empty simply omits it.
    useEffect(() => {
        runSafely('HomePage.getShowcase', getShowcase);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <AppShell>
            {/* Hero: fixed strip on mobile, full-bleed with overlaid headline on desktop.
                The overlay now runs at every width — mobile gets the two calls to
                action sitting on a bottom-up gradient (which keeps them legible over
                the campaign image), desktop keeps the centred headline with
                the buttons beneath it. */}
            <div className="relative w-full h-[240px] lg:h-[70vh]">
                {/* Same fix as the support hero: `object-cover` was scoped to lg, so
                    below it the image was stretched to the box rather than fitted. The
                    animated banner is ~1.9:1 against a box that runs ~1.6 on a narrow
                    phone and ~2.0 on a wide one, so cover crops it only slightly either
                    way. Centre crop puts the luggage mid-frame; the banner's own
                    "BONIA / NU TRANSIT" lockup sits far left and is the first thing
                    a narrower viewport loses. */}
                <img src='/images/bonia/gif-banner.webp' className='w-full h-full object-cover' alt="" />
                <div className={
                    'absolute inset-0 flex flex-col items-center justify-end px-4 pb-5 ' +
                    'bg-gradient-to-t from-brand-900/75 via-brand-900/20 to-transparent ' +
                    'lg:justify-center lg:pb-0 lg:bg-none lg:bg-brand-900/25'
                }>
                    <h1 className="hidden lg:block font-display text-display-lg text-white m-0 mb-10 tracking-wide">
                        {t("commodity")}
                    </h1>
                    <div className="flex gap-3 lg:gap-5">
                        {HERO_CTAS.map(({ key, labelKey, path }) => (
                            <button
                                key={key}
                                type='button'
                                onClick={() => navigate(path)}
                                className={HERO_CTA_CLASS}
                            >
                                {t(labelKey)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <Container className="lg:py-20">
                {/* Section rule: keep the mobile divider, quieter on desktop */}
                <div className='flex my-4 lg:hidden'>
                    <div className='w-full bg-brand-900 h-[3px] m-auto ml-4'></div>
                    <div className='mx-4 text-[20px]'>{t("commodity")}</div>
                    <div className='w-full bg-brand-900 h-[3px] m-auto mr-4'></div>
                </div>

                {/*
                  Below lg: an auto-advancing slider instead of three stacked
                  images. Each slide is a fixed 16/9 box with object-cover
                  rather than the images' natural height — the storefronts run
                  1.6:1 and 1.5:1 while bonia_sign is 4:3, so natural heights
                  would make the track jump on every advance.

                  At lg the three-up grid is kept: there is nothing to slide
                  when all three already fit side by side.
                */}
                <div className='commodity-carousel mx-8 mb-8 lg:hidden'>
                    <Carousel autoplay autoplaySpeed={4000} draggable>
                        {COMMODITY_IMAGES.map(src => (
                            <div key={src}>
                                <img src={src} className='w-full aspect-[16/9] object-cover' alt="" />
                            </div>
                        ))}
                    </Carousel>
                </div>

                <div className='hidden lg:grid lg:grid-cols-3 lg:gap-8'>
                    {COMMODITY_IMAGES.map(src => (
                        <img key={src} src={src} className='w-full h-[420px] object-cover' alt="" />
                    ))}
                </div>
            </Container>

            {/*
              Product showcase — two across on every width. Two products in a
              two-column grid is the standard mobile catalogue shape, so the
              desktop grid carries down unchanged and only the spacing and type
              scale step down.

              Renders nothing at all until the catalogue has loaded, and nothing
              if the request failed or returned too few products — Home must not
              break because of a decorative section. That is why the guard is on
              length rather than on a loading flag: there is no spinner state to
              get stuck in.
            */}
            {showcase.length === SHOWCASE_COUNT && (
                <div className='bg-brand-100'>
                    <Container className='py-10 lg:py-20'>
                        {/* Mobile reuses the rule-and-label divider the section above
                            uses, so the two mobile sections read as siblings. Desktop
                            drops the rules for the centred display heading.
                            Labelled "featured", not "commodity": that heading is
                            already on screen above this on mobile. */}
                        <div className='flex items-center mb-6 lg:mb-12 lg:justify-center'>
                            <div className='w-full bg-brand-900 h-[3px] ml-4 lg:hidden' aria-hidden='true'></div>
                            <h2 className={
                                'my-0 mx-4 text-[20px] font-normal whitespace-nowrap text-brand-900 ' +
                                'lg:mx-0 lg:font-display lg:text-display-md lg:tracking-wide'
                            }>
                                {t("featured")}
                            </h2>
                            <div className='w-full bg-brand-900 h-[3px] mr-4 lg:hidden' aria-hidden='true'></div>
                        </div>

                        <div className='grid grid-cols-2 gap-4 px-4 lg:gap-10 lg:px-0'>
                            {showcase.map(e => (
                                <button
                                    key={e.id}
                                    type='button'
                                    onClick={() => navigate("/order")}
                                    className={
                                        'group block w-full text-left bg-transparent border-0 p-0 cursor-pointer ' +
                                        'transition-opacity duration-150 active:opacity-80 ' +
                                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 ' +
                                        'focus-visible:outline-accent-500'
                                    }
                                >
                                    {/* The scale lives on the image inside a clipped box, so the
                                        tile's own footprint never shifts and neighbours stay put. */}
                                    <div className='overflow-hidden bg-brand-50'>
                                        <img
                                            src={e.url}
                                            alt={e.name}
                                            className={
                                                'w-full aspect-[4/5] object-cover ' +
                                                'transition-transform duration-700 ease-out group-hover:scale-[1.03]'
                                            }
                                        />
                                    </div>
                                    <div className='pt-3 lg:pt-6'>
                                        <div className='text-[10px] uppercase tracking-[0.12em] text-brand-600 lg:text-xs lg:tracking-[0.18em]'>
                                            {e.seller}
                                        </div>
                                        {/* Clamped to two lines so a long product name cannot
                                            push one tile's price out of line with the other's. */}
                                        <div className={
                                            'font-display text-[15px] text-brand-900 mt-1 line-clamp-2 ' +
                                            'lg:text-xl lg:mt-2 lg:line-clamp-none ' +
                                            'group-hover:underline underline-offset-4'
                                        }>
                                            {e.name}
                                        </div>
                                        <div className='font-display text-[14px] text-brand-900 mt-1 lg:text-lg lg:mt-2'>
                                            RM {formatNumber(e.price)}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </Container>
                </div>
            )}
            {/* <div className='flex my-4'>
                <div className='w-full bg-brand-900 h-[3px] m-auto ml-4'></div>
                <div className='mx-4 text-[20px]'>{t("commission")}</div>
                <div className='w-full bg-brand-900 h-[3px] m-auto mr-4'></div>
            </div> */}
            {/* <div className='mb-8'>
                <AutoSwipe />
            </div> */}

            {/* Lucky Spin Floating Action Button */}
            {/* <button
                type="button"
                onClick={() => navigate('/lucky-spin')}
                className="fixed bottom-[80px] right-4 bg-[#d4af37] text-white p-4 rounded-full shadow-xl hover:scale-105 transition-transform z-50 flex items-center justify-center"
                style={{ width: 60, height: 60 }}
            >
                <FontAwesomeIcon icon={faGift} size="2x" />
            </button> */}
        </AppShell>
    );
}

export default HomePage;
